// valuePass/admin/src/main.tsx
//
// Application entry point.
//
// ── Boot sequence ─────────────────────────────────────────────────────────────
//
//   1. initFirebase()
//      Fetches the Firebase config (from /__/firebase/init.json in production,
//      or from VITE_FIREBASE_* env vars in development) and initialises the
//      Firebase app, Firestore, and Auth singletons.
//
//      This MUST complete before any component mounts, because:
//        • AppShell calls getFirebaseAuth() to attach the auth listener
//        • Firestore hooks call getDb() to open real-time listeners
//        • The API client calls getIdToken() to attach Bearer tokens
//
//      If initFirebase() throws (missing config, network error) a full-page
//      error screen is rendered instead of the app so the failure is visible
//      and actionable rather than a cryptic blank screen.
//
//   2. React.createRoot().render(<App />)
//      Mounts the root React tree only after Firebase is ready.
//      App.tsx provides QueryClientProvider + RouterProvider.
//
// ── Error handling ────────────────────────────────────────────────────────────
//
//   Any error thrown during Firebase initialisation is caught and rendered as
//   a minimal, styled error screen that:
//     • Does NOT depend on any React component (avoids rendering a broken tree)
//     • Shows the error message so developers can diagnose the problem
//     • Provides a "Copy error" button for easy sharing
//     • Includes a link to the Firebase Console for config reference
//
// ── Environment ───────────────────────────────────────────────────────────────
//
//   Production : config from /__/firebase/init.json (Firebase Hosting)
//   Development: config from .env.local (copy .env.example → .env.local)
//   Emulators  : set VITE_USE_EMULATORS=true in .env.local
//
// ─────────────────────────────────────────────────────────────────────────────

import React from "react";
import ReactDOM from "react-dom/client";
import { initFirebase } from "@/lib/firebase";
import App from "./App";
import "./index.css";

// ── Root element ──────────────────────────────────────────────────────────────

const rootEl = document.getElementById("root");

if (!rootEl) {
  // This should never happen with a correct index.html, but guard anyway so
  // the error message is clear rather than a null-dereference crash.
  throw new Error(
    '[main] Could not find #root element. ' +
    'Make sure index.html contains <div id="root"></div>.',
  );
}

// ── Bootstrap ─────────────────────────────────────────────────────────────────

/**
 * Renders a minimal, dependency-free error screen when Firebase fails to
 * initialise. Uses only vanilla DOM APIs so it always works even if React or
 * Tailwind have not loaded yet.
 *
 * @param err  The error thrown by initFirebase()
 */
function renderInitError(err: unknown): void {
  const message =
    err instanceof Error
      ? err.message
      : typeof err === "string"
      ? err
      : "Unknown initialisation error.";

  rootEl!.innerHTML = `
    <div style="
      min-height: 100vh;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      padding: 24px;
      background: #f0f2f5;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    ">
      <div style="
        background: white;
        border-radius: 12px;
        box-shadow: 0 2px 8px rgba(0,0,0,0.08);
        padding: 32px;
        max-width: 520px;
        width: 100%;
        border-left: 4px solid #dc2626;
      ">
        <!-- Logo -->
        <div style="display: flex; align-items: center; gap: 12px; margin-bottom: 24px;">
          <div style="
            width: 40px; height: 40px;
            background: #1b4d3e;
            border-radius: 50%;
            display: flex; align-items: center; justify-content: center;
            font-weight: bold; font-size: 18px; color: #e2c068;
          ">vp</div>
          <div>
            <div style="font-size: 16px; font-weight: 700; color: #1a1a2e;">
              <span style="color: #1b4d3e;">Value</span>Pass Admin
            </div>
            <div style="font-size: 12px; color: #666;">Initialisation failed</div>
          </div>
        </div>

        <!-- Error heading -->
        <h1 style="font-size: 18px; font-weight: 700; color: #dc2626; margin: 0 0 8px;">
          Firebase could not be initialised
        </h1>

        <!-- Error message -->
        <p style="font-size: 13px; color: #666; line-height: 1.6; margin: 0 0 16px;">
          The app failed to start because the Firebase configuration could not
          be loaded. This usually means a missing or incorrect
          <code style="background:#f5f5f5; padding: 1px 4px; border-radius: 3px; font-size: 12px;">.env.local</code>
          file when running locally.
        </p>

        <!-- Error details box -->
        <pre id="vp-error-msg" style="
          background: #fef2f2;
          border: 1px solid #fecaca;
          border-radius: 8px;
          padding: 12px;
          font-size: 12px;
          color: #dc2626;
          white-space: pre-wrap;
          word-break: break-word;
          line-height: 1.5;
          margin: 0 0 20px;
        ">${message.replace(/</g, '&lt;').replace(/>/g, '&gt;')}</pre>

        <!-- Actions -->
        <div style="display: flex; gap: 10px; flex-wrap: wrap;">
          <button
            onclick="navigator.clipboard.writeText(document.getElementById('vp-error-msg').textContent).then(() => this.textContent = 'Copied!')"
            style="
              padding: 8px 14px;
              background: white;
              border: 1px solid #d1d5db;
              border-radius: 8px;
              font-size: 13px;
              font-weight: 600;
              color: #374151;
              cursor: pointer;
              transition: background 0.15s;
            "
            onmouseover="this.style.background='#f9fafb'"
            onmouseout="this.style.background='white'"
          >
            Copy error
          </button>

          <a
            href="https://console.firebase.google.com/project/valuepass/settings/general"
            target="_blank"
            rel="noopener noreferrer"
            style="
              padding: 8px 14px;
              background: #1b4d3e;
              border: 1px solid #1b4d3e;
              border-radius: 8px;
              font-size: 13px;
              font-weight: 600;
              color: white;
              cursor: pointer;
              text-decoration: none;
              display: inline-flex;
              align-items: center;
              gap: 6px;
            "
          >
            Open Firebase Console →
          </a>
        </div>

        <!-- Help text -->
        <div style="margin-top: 20px; padding-top: 16px; border-top: 1px solid #f0f0f0;">
          <p style="font-size: 12px; color: #9ca3af; margin: 0; line-height: 1.6;">
            <strong>Local development:</strong> Copy
            <code style="background:#f5f5f5; padding:1px 4px; border-radius:3px;">.env.example</code>
            to
            <code style="background:#f5f5f5; padding:1px 4px; border-radius:3px;">.env.local</code>
            and fill in your Firebase project credentials from the Firebase Console
            under <em>Project Settings → Your apps → SDK setup and configuration</em>.
          </p>
          <p style="font-size: 12px; color: #9ca3af; margin: 8px 0 0; line-height: 1.6;">
            <strong>Production:</strong> Ensure the admin app is deployed to Firebase
            Hosting so that
            <code style="background:#f5f5f5; padding:1px 4px; border-radius:3px;">/__/firebase/init.json</code>
            is served automatically.
          </p>
        </div>
      </div>
    </div>
  `;
}

/**
 * Initialises Firebase and mounts the React app.
 *
 * The async IIFE pattern is used because the module top-level cannot be
 * `await`-ed in all target environments. The try/catch ensures that any
 * Firebase init error is surfaced visually rather than silently swallowed.
 */
(async () => {
  try {
    // ── Step 1: Firebase initialisation ────────────────────────────────────
    //
    // Must complete before any React component mounts. See lib/firebase.ts
    // for the full config resolution strategy (hosted init.json vs env vars).
    await initFirebase();

    // ── Step 2: Mount the React application ────────────────────────────────
    //
    // createRoot is the React 18 concurrent-mode entry point.
    // We render once; subsequent updates are driven by state/context changes.
    const root = ReactDOM.createRoot(rootEl!);

    root.render(<App />);

    // ── Step 3: Remove loading indicator ───────────────────────────────────
    //
    // If the HTML shell contains a static loading indicator (e.g., a spinner
    // in a <noscript> block or a CSS animation on #root::before), remove it
    // now that React has taken over the DOM.
    rootEl!.removeAttribute("data-loading");

    if (import.meta.env.DEV) {
      console.info(
        "%c[ValuePass Admin]%c Firebase initialised. React app mounted.",
        "color: #1b4d3e; font-weight: bold;",
        "color: inherit;",
      );
    }

  } catch (err) {
    // Render a user-visible error screen.
    // We do NOT rethrow — an unhandled promise rejection would produce a
    // less informative "Uncaught (in promise)" error in the console.
    renderInitError(err);

    console.error(
      "[ValuePass Admin] Fatal initialisation error:",
      err,
    );
  }
})();
